


import { useQuery } from "@apollo/client";
import { Alert, Box, Card, CardContent, Typography } from "@mui/material";
import { useState } from "react";
import DoughnutChart from "~/components/common/charts/DoughnutChart";
import { KpiChartCancellationRateQuery } from "~/graphql/__generated__/graphql";
import { GET_CANCELATION_RATE_CHART_QUERY } from "../kpiQueries";
import { ChartData } from "chart.js";
import SkeletonDoughnutChart from "~/components/common/charts/SkeletonDoughnutChart";
import { StatusPalate } from "~/utils/statuses";
import { includes, partition } from "lodash";

interface Props {
  startTime?: Date;
  endTime?: Date;
  providerId?: number;
  serviceLineId?: number;
}

export const CancelationRateChart: React.FC<Props> = ({
  startTime,
  endTime,
  serviceLineId,
  providerId,
}) => {

  const [error, setError] = useState('');
  const { data, loading } = useQuery<KpiChartCancellationRateQuery>(
    GET_CANCELATION_RATE_CHART_QUERY, { 
      variables: {
        input: {
          startTime: startTime?.toISOString(),
          endTime: endTime?.toISOString(),
          serviceLineId,
          providerId,
        }
      },
      onError(error) {
        console.error('KpiChartCancellationRateQuery Error: ->', error);
        setError('Something when wrong when loading the data');
      },
      onCompleted() {
        setError('');
      }
    },  
  );

  const cancellationDataGraph = data?.kpiCancellationRate;

  const totalCancelationRate = cancellationDataGraph?.totalCancellationRate ?? 0;

  // both cancellation reasons palette have until 5 colors, at some point if the organization has more than 5 colors they will repeat.
  const providerGrayPalette = ["#B0B0B0", "#A5A5A5", "#9A9A9A", "#858585","#7A7A7A"];
  const nonProviderPalette = StatusPalate.canceled;

  const generateColors = (keys: string[]) => {
    const [providerCancel, nonProviderCancel] = partition(keys, (t) => includes(t, "Provider"));

    const providerColors = providerCancel.map((_, i) => providerGrayPalette[i % providerGrayPalette.length]);
    const nonProviderColors = nonProviderCancel.map((_, i) => nonProviderPalette[i % nonProviderPalette.length]);

    return providerColors.concat(nonProviderColors);
  };

  const chartColors = generateColors(Object.keys(cancellationDataGraph?.cancellationRates ?? []));

  const chartData = {
    "labels": Object.keys(cancellationDataGraph?.cancellationRates ?? []),
    "datasets": [
      {
        "data": Object.values(cancellationDataGraph?.cancellationRates ?? []),
        "backgroundColor": chartColors
      }
    ]
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {loading ? <Box sx={{ maxWidth: '80%' }}><SkeletonDoughnutChart title="Cancellation Rate" /></Box> : (
            error ? (
              <Alert severity="error">
                {error}
              </Alert>
            ) : (
              !chartData || !chartData?.datasets?.length ? (
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Alert severity="warning" sx={{ width: '100%' }}>
                    No Data
                  </Alert>
                </Box>
              ) : (
                <>
                  <Typography fontWeight={500}>Cancellation Rate</Typography>
                  <DoughnutChart
                    data={chartData as ChartData<"doughnut">}
                    centerText={`${totalCancelationRate}%`}
                    loading={false}
                    legendPosition="bottom"
                  />
                </>
              )
            )
          )}
      </CardContent>
    </Card>
  )
}